import { CallTransferStatus } from '@api/generated/graphql';
import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import getTransferStatus from '@api/queries/getTransferStatus';
import appManager from '@app/appManager';
import AppModule from '@app/lib/AppModule';
import { authState } from '@modules/AuthModule/state';
import DiagnosticsModule, {
  MetricNames,
  MetricTags,
  OperationNames,
  CallDirection,
  CallConnectionErrorTypes,
} from '@modules/DiagnosticsModule';
import { serviceState } from '@modules/ServiceModule/workspace/lib/state';
import SoftPhoneModule from '@modules/SoftPhoneModule';
import { CallEvent } from '@modules/SoftPhoneModule/types';
import TasksModule from '@modules/TasksModule';
import ToastModule from '@modules/ToastModule';
import { ColdCallTransferDetails, WarmTransferContext } from './types';
import { OutboundCallEntity } from '@lib/types/common-call-types';

/**
 * TransfersModule
 *
 * Manages call transfers within the app. Handles both internal
 * transfers (to other agents) and external transfers (to external phone numbers),
 * providing real-time status updates and user feedback through toast notifications.
 *
 * Features:
 * - Initiating call transfers to agents or external numbers
 * - Monitoring transfer status and providing real-time updates
 * - Managing transfer state in tasks
 * - Handling transfer completion and cleanup
 * - Restoring in-progress transfers on application load
 */
export class TransfersModule extends AppModule {
  /**
   * Sets up event listeners for call transfer operations during module initialization.
   *
   * Behavior:
   * - Registers handlers for user status initialization to restore in-progress transfers
   * - Sets up call event listeners for phone call lifecycle (placed, answered, ended, failed, missed)
   * - Enables automatic recovery of warm transfer states on application reload
   */
  override preload(): void {
    this.app.events.on('user.status.initialized', () => {
      // process any in-progress transfer tasks when the user status is initialized
      this.handleInProgressTransfersOnLoad();
    });

    this.app.events.on('phone.call.placed', (ev: CallEvent) => {
      // handle call placed event
      this.handlePhoneCallPlaced(ev);
    });

    this.app.events.on('phone.call.answered', (ev: CallEvent) => {
      // handle call answered event
      this.handlePhoneCallAnswered(ev);
    });

    this.app.events.on('phone.call.ended', (ev: CallEvent) => {
      this.handlePhoneCallEnded(ev);
    });

    this.app.events.on('phone.call.failed', (ev: CallEvent) => {
      this.handlePhoneCallEnded(ev);
    });

    this.app.events.on('phone.call.missed', (ev: CallEvent) => {
      this.handlePhoneCallEnded(ev);
    });
  }

  /**
   * Initiates a call transfer for a given task.
   *
   * Behavior:
   * - For internal transfers (when agent is identified), sets up transfer status monitoring
   * - For external transfers, shows a notification and delegates to the softphone
   * - For queue transfers, updates task with transfer details and logs the operation
   *     (follows existing softphone transfer setup without additional status monitoring)
   * - Automatically detects if a phone number belongs to an agent's personal line
   * - Updates task attributes with transfer status information
   */
  public async makeColdCallTransfer({
    taskOrCallId,
    transferToNumber,
    agentId,
    transferType,
    queueId,
    queueName,
  }: ColdCallTransferDetails): Promise<void> {
    const agentContactCenterId = this.getAgentContactCenterId(
      agentId,
      transferToNumber,
    );

    const task =
      TasksModule.getTask(taskOrCallId) ??
      TasksModule.getTaskByCallId(taskOrCallId);

    if (!task) {
      console.error('Task not found for transfer:', taskOrCallId);
      return;
    }

    // Check if this is a queue transfer (when a feature flag is enabled)
    const isTransferringToQueue =
      FeatureFlagModule.isEnabled(
        FeatureFlagKeys.featureInboxCallTransferToQueue,
      ) && transferType === OutboundCallEntity.Queue;

    if (agentContactCenterId) {
      // Internal agent transfer: setup status monitoring for transfer lifecycle
      TasksModule.setTaskAttribute(task.id, 'transferStatus', {
        inProgress: true,
        agentId: agentContactCenterId,
        initiatedAt: new Date(),
      });
      this.handleInProgressTransfer(task.id);
    } else if (isTransferringToQueue) {
      // Queue transfer: store transfer metadata
      // Note: Queue transfers leverage the existing softphone transfer mechanism
      // without additional status polling. The call will be routed to the queue
      // via the standard transfer flow, and the task is updated with queue details
      TasksModule.setTaskAttribute(task.id, 'transferStatus', {
        initiatedAt: new Date(),
        transferType,
        queueId,
        queueName,
      });

      // show toast and wrap up for external
      ToastModule.showToast({
        title: 'Transfer completed.',
        status: 'success',
        duration: 5000,
      });

      this.app.logger.info(
        `Cold transfer initiated to queue: "${queueName}" (ID: ${queueId})`,
        {
          taskId: task.id,
          callId: task.attributes?.callId,
          queueId,
          queueName,
        },
      );
    } else {
      // External transfer: show notification and proceed with standard transfer
      ToastModule.showToast({
        title: 'Transfer completed.',
        toastBody: '',
        status: 'success',
        duration: 5000,
      });
    }

    // transfer call
    SoftPhoneModule.transferNumber(transferToNumber);
  }

  /**
   * Initiates a warm call transfer by establishing a warm up call before transferring.
   *
   * @param taskId - The ID of the task associated with the original call being transferred
   * @param transferToNumber - The phone number to transfer the call to
   * @param agentId - (Optional) The ID of the agent to transfer to (for internal transfers)
   *
   * Behavior:
   * - Creates a warm transfer context to track the transfer state
   * - Makes an outbound call to the transfer target for warm up call
   * - Updates task attributes with warm transfer context for state management
   * - Handles both internal (agent) and external (phone number) transfers
   *
   * Note:
   * The warm transfer will only be completed once the user clicks on the "Complete Transfer" button
   * @see {@link completeWarmCallTransfer} - To complete the warm transfer process
   */
  public async startWarmCallTransfer(
    taskId: string,
    transferToNumber: string,
    agentId?: string,
  ): Promise<void> {
    // put the main call on hold
    const originalTask = TasksModule.getTask(taskId);
    if (!originalTask) {
      console.error(
        'Original task not found for warm transfer. Task ID: ',
        taskId,
      );
      return;
    }

    const agentContactCenterId = this.getAgentContactCenterId(
      agentId,
      transferToNumber,
    );

    const agentName = this.getAgentName(agentContactCenterId);

    // create a new warmTransferContext object to be added into two tasks
    const warmTransferContext = {
      isWarmUpCallConnected: false,
      originalCallId: originalTask.attributes?.callId ?? '',
      warmUpCallId: null,
      targetAgentId: agentContactCenterId,
      targetPhoneNumber: transferToNumber,
      initiatedAt: new Date(),
    } as WarmTransferContext;

    TasksModule.setTaskAttribute(
      originalTask.id,
      'warmTransferContext',
      warmTransferContext,
    );

    // create a new outbound task for the warm transfer with context attached in attributes
    this.makeOutboundCall(transferToNumber, warmTransferContext, agentName);
  }

  /**
   * Completes a warm call transfer by ending the warm up call and transferring the original call.
   *
   * @param warmTransferContext - The context object containing transfer state information
   *
   * Behavior:
   * - Ends the warm-up call to the transfer target
   * - Initiates a cold transfer of the original call to the target
   */
  public completeWarmCallTransfer(
    warmTransferContext: WarmTransferContext,
  ): void {
    // end the warm up call
    SoftPhoneModule.endCall(warmTransferContext.warmUpCallId ?? '');

    this.makeColdCallTransfer({
      taskOrCallId: warmTransferContext.originalCallId,
      transferToNumber: warmTransferContext.targetPhoneNumber,
      agentId: warmTransferContext.targetAgentId ?? undefined,
      transferType: OutboundCallEntity.Employees,
    });
  }

  /**
   * Creates an outbound call for warm transfer warm up call.
   *
   * @param phoneNumber - The phone number to call for warm up call
   * @param warmTransferContext - The context object containing transfer state information
   * @param transferToAgentName - (Optional) The name of the agent being called
   *
   * Behavior:
   * - Determines the appropriate outbound calling method based on feature flags
   * - Uses either SoftPhone or QuickCall API depending on configuration
   * - Handles error cases with user feedback and cleanup
   * - Attaches warm transfer context to the warm up call task
   */
  private async makeOutboundCall(
    phoneNumber: string,
    warmTransferContext: WarmTransferContext,
    transferToAgentName?: string,
  ): Promise<void> {
    const fixOutboundCallerIdErrors = FeatureFlagModule.isEnabled(
      FeatureFlagKeys.fixOutboundCallerIdErrors,
    );

    try {
      if (!phoneNumber) {
        console.error('Phone number is invalid');
        throw new Error('Phone number is invalid');
      }

      const taskAttributes = {
        warmTransferContext,
      };

      const agentPhoneNumber = this.getAgentPhoneNumber() ?? '';
      const agentName = fixOutboundCallerIdErrors
        ? ''
        : this.getCurrentAgentName() ?? '';

      this.makeOutboundViaSoftPhone(
        phoneNumber,
        agentPhoneNumber,
        agentName,
        taskAttributes,
        transferToAgentName,
      );
    } catch (error) {
      DiagnosticsModule.incrementCounter(MetricNames.ERROR_TOTAL, {
        [MetricTags.OPERATION_NAME]: OperationNames.CALL_CONNECTION,
        [MetricTags.DIRECTION]: CallDirection.Outbound.toLowerCase(),
        [MetricTags.ERROR_TYPE]: CallConnectionErrorTypes.WARM_TRANSFER,
      });
      const toastTitle =
        'We encountered an issue while making an outbound call. Please try again.';
      ToastModule.showToast({
        title: toastTitle,
        status: 'critical',
      });
      DiagnosticsModule.reportBackgroundError(
        error,
        'Failed to make an outbound call for a warm transfer.',
        {
          extra: {
            phoneNumber,
            warmTransferContext,
            transferToAgentName,
          },
        },
      );

      this.deleteWarmTransferContexts(warmTransferContext);
    }
  }

  /**
   * Updates warm transfer context across related tasks (original and warm up calls).
   *
   * @param originalCallId - The call ID of the original customer call
   * @param warmUpCallId - The call ID of the warm up call
   * @param updates - Partial updates to apply to the warm transfer context
   *
   * Behavior:
   * - Finds both original and warm up call tasks
   * - Merges updates with existing warm transfer context
   * - Synchronizes context across both related tasks
   * - Handles cases where tasks might not be found
   */
  private updateWarmTransferContexts(
    originalCallId: string,
    warmUpCallId: string,
    updates: Partial<WarmTransferContext>,
  ): void {
    const originalTask = TasksModule.getTaskByCallId(originalCallId);
    if (!originalTask) {
      console.error('Original task not found for warm transfer context update');
      return;
    }

    const warmUpCallTask = TasksModule.getTaskByCallId(warmUpCallId);
    if (!warmUpCallTask) {
      console.error(
        'Warm up call task not found for warm transfer context update',
      );
      return;
    }

    const warmTransferContext: WarmTransferContext = {
      ...originalTask.attributes.warmTransferContext,
      ...updates,
    };

    // update the task with the new warm transfer context
    TasksModule.setTaskAttribute(
      originalTask.id,
      'warmTransferContext',
      warmTransferContext,
    );

    TasksModule.setTaskAttribute(
      warmUpCallTask.id,
      'warmTransferContext',
      warmTransferContext,
    );
  }

  /**
   * Cleans up warm transfer context from all related tasks.
   *
   * This is used to cleanup warm transfer state in case the warm transfer
   * is unsuccessful such as warmup call fails, etc.
   *
   * @param warmTransferContext - The warm transfer context to clean up
   *
   * Behavior:
   * - Removes warm transfer context from original call task
   * - Removes warm transfer context from warm up call task
   * - Handles cases where context or tasks might be null/undefined
   */
  private deleteWarmTransferContexts(
    warmTransferContext: WarmTransferContext | undefined | null,
  ): void {
    if (!warmTransferContext) {
      return;
    }
    const originalTask = TasksModule.getTaskByCallId(
      warmTransferContext.originalCallId,
    );
    const warmUpCallTask = TasksModule.getTaskByCallId(
      warmTransferContext.warmUpCallId ?? '',
    );

    if (originalTask) {
      TasksModule.setTaskAttribute(
        originalTask.id,
        'warmTransferContext',
        undefined,
      );
    }
    if (warmUpCallTask) {
      TasksModule.setTaskAttribute(
        warmUpCallTask.id,
        'warmTransferContext',
        undefined,
      );
    }
  }

  /**
   * Determines if a call is part of a warm transfer warm up call.
   *
   * @param callId - The call ID to check
   * @param remoteIdentity - The remote party identifier (phone number)
   * @returns True if the call is a warm-up call, false otherwise
   *
   * Behavior:
   * - Checks if call ID matches the warm-up call ID in any task's context
   * - Checks if remote identity matches the target phone number
   * - Handles variations in phone number formatting from different sources
   * - Used to identify consultation calls for proper context management
   */
  private isWarmUpCall(callId: string, remoteIdentity: string): boolean {
    // Check if the call is a warm-up call by matching the call ID and remote identity
    const task = TasksModule.getTaskByCallId(callId);

    // either has warmTransferContext with warmUpCallId
    const hasWarmUpCallId =
      task?.attributes?.warmTransferContext?.warmUpCallId === callId;

    // or has warmTransferContext with targetPhoneNumber that matches the remote identity
    const hasTargetPhoneNumber =
      task?.attributes?.warmTransferContext?.targetPhoneNumber.includes(
        remoteIdentity,
      ) ||
      remoteIdentity.includes(
        task?.attributes?.warmTransferContext?.targetPhoneNumber, // in case remote identity call comes with extra information like "Device QuickCall (+1234567890)"
      );

    return hasWarmUpCallId || hasTargetPhoneNumber;
  }

  /**
   * Resolves the contact center user ID for a transfer target.
   *
   * @param agentId - The agent ID (may be contact center user ID or regular user ID)
   * @param transferToNumber - The phone number being transferred to
   * @returns The contact center user ID if found, null for external transfers
   *
   * Behavior:
   * - First checks if agentId is already a contact center user ID
   * - Maps regular user ID to contact center user ID if needed
   * - Falls back to matching phone number with agent personal lines
   * - Returns null for external (non-agent) transfers
   */
  private getAgentContactCenterId(
    agentId: string | undefined | null,
    transferToNumber: string,
  ): string | null {
    // determine if transfer to agent or number
    const hasAgent = !!agentId;
    const userById = serviceState.userById;
    const userByContactCenterUserId = serviceState.userByContactCenterUserId;

    // already given contactCenterUserId
    if (hasAgent && userByContactCenterUserId[agentId]) {
      return agentId;
    }

    // we need the agent contactcenter user id
    let agentContactCenterId = hasAgent
      ? userById[agentId]?.contactCenterUserId
      : '';

    // cannot find the agent via ID, try to match the external number to a user personal line
    agentContactCenterId ||= Object.values(userById).find((user) =>
      user.personalLine?.phoneNumber?.includes(transferToNumber),
    )?.contactCenterUserId;

    return agentContactCenterId || null;
  }

  /**
   * Gets the current agent's phone number for outbound calls.
   *
   * @returns The agent's personal line phone number, or undefined if not available
   *
   * Behavior:
   * - Retrieves phone number from the authenticated user's personal line
   * - Used for caller ID when making outbound calls via QuickCall API
   * - Returns undefined if user has no personal line configured
   */
  private getAgentPhoneNumber() {
    const user = authState.user;

    return user?.personalLine?.phoneNumber;
  }

  /**
   * Gets the current agent's display name for outbound calls.
   *
   * @returns The agent's name, or undefined if not available
   *
   * Behavior:
   * - Retrieves name from the authenticated user profile
   * - Used for caller ID when making outbound calls via QuickCall API
   * - Returns undefined if user has no name configured
   */
  private getCurrentAgentName(): string | undefined {
    const user = authState.user;

    return user?.name;
  }

  /**
   * Gets the display name for an agent by their contact center user ID.
   *
   * @param agentContactCenterId - The contact center user ID of the agent
   * @returns The agent's display name, or undefined if not found
   *
   * Behavior:
   * - Looks up agent information using contact center user ID
   * - Returns the agent's name for display purposes
   * - Used in warm transfer scenarios to show who is being called
   */
  private getAgentName(
    agentContactCenterId: string | null,
  ): string | undefined {
    if (!agentContactCenterId) {
      return undefined;
    }

    const userByContactCenterUserId = serviceState.userByContactCenterUserId;

    const agent = userByContactCenterUserId[agentContactCenterId];

    return agent?.name;
  }

  /**
   * Makes an outbound call using the SoftPhone module.
   *
   * @param phoneNumber - The phone number to call
   * @param customTaskAttributes - Custom attributes to attach to the created task
   * @param name - (Optional) Display name for the call target
   *
   * Behavior:
   * - Uses SoftPhone module for direct call initiation
   * - Attaches custom task attributes for context tracking
   * - Supports display name for better user experience
   * - Preferred method when MFE features are enabled
   */
  private async makeOutboundViaSoftPhone(
    targetPhoneNumber: string,
    callerPhoneNumber: string,
    callerName: string,
    customTaskAttributes: any,
    targetAgentName?: string,
  ) {
    // note that we don't have to call SoftPhoneModule.prepareOutboundCall here as it's already done within the callNumber method

    return SoftPhoneModule.callNumberWithCallId(
      targetPhoneNumber,
      {
        name: callerName,
        number: callerPhoneNumber,
        tenantId: null,
      },
      targetAgentName,
      null,
      customTaskAttributes,
    );
  }

  /**
   * Monitors the status of an ongoing transfer and handles completion.
   *
   * @param taskId - The ID of the task being transferred
   *
   * Behavior:
   * - Polls transfer status every 10 seconds for unknown/in-progress transfers
   * - On transfer failure: Updates task status and shows error toast
   * - On transfer success: Deletes the task and shows success toast
   * - Handles early returns for invalid or completed transfers
   */
  private async handleInProgressTransfer(taskId: string): Promise<void> {
    const task = TasksModule.getTask(taskId);
    // early return in case of no task or task transferStatus not inProgress
    if (!task || !task.attributes?.transferStatus?.inProgress) {
      return;
    }

    const status = await getTransferStatus(
      task.id,
      task.attributes.transferStatus.agentId,
    );

    if (
      status === CallTransferStatus.Unknown ||
      status === CallTransferStatus.InProgress
    ) {
      // unknown: it's possible that conversation has not been updated just yet
      // in progress: it's possible that the call is still ringing for the transferee
      setTimeout(() => {
        this.handleInProgressTransfer(taskId);
      }, 10000);
    } else if (status === CallTransferStatus.Failure) {
      // we know that the transfer is failed, show error toast and keep close&classify
      // as is
      TasksModule.setTaskAttribute(task.id, 'transferStatus', {
        ...task.attributes.transferStatus,
        inProgress: false,
      });
      ToastModule.showToast({
        title:
          'The transfer was not answered. Please close & classify the call.',
        status: 'critical',
        duration: 5000,
      });
    } else if (status === CallTransferStatus.Successful) {
      // we know that the transfer is successful, show success toast
      // and set task status to completed with close and classify properly set
      TasksModule.setTaskAttribute(task.id, 'transferStatus', {
        ...task.attributes.transferStatus,
        inProgress: false,
      });
      // no need to complete the task (if completed, this would prevent
      // the second agent from close-classifying as we would set the interaction record)
      TasksModule.deleteTask(task.id);
      ToastModule.showToast({
        title: 'Call Transferred',
        status: 'success',
        duration: 5000,
      });
    }
  }

  /**
   * Restores monitoring for any transfers that were in progress when the application loads.
   *
   * Behavior:
   * - Only active when `featureInboxCallWarmTransfers` feature flag is enabled
   * - Scans all existing tasks for in-progress transfers
   * - Resumes monitoring for each found transfer
   */
  private async handleInProgressTransfersOnLoad(): Promise<void> {
    const featureInboxCallWarmTransfers = FeatureFlagModule.isEnabled(
      FeatureFlagKeys.featureInboxCallWarmTransfers,
    );
    if (featureInboxCallWarmTransfers) {
      TasksModule.getTasks().forEach((task) => {
        if (task.attributes?.transferStatus?.inProgress) {
          this.handleInProgressTransfer(task.id);
        }
      });
    }
  }

  /**
   * Handles the phone call placed event for warm transfer tracking.
   *
   * @param event - The call event containing call ID and remote identity information
   *
   * Behavior:
   * - Identifies if the placed call is part of a warm transfer warm up call
   * - Updates warm transfer context with the warm up call call ID
   * - Synchronizes context across original and warm up call tasks
   * - Enables proper tracking of warm transfer call lifecycle
   */
  private handlePhoneCallPlaced(event: CallEvent): void {
    // we need to check if the call that is placed is a warm transfer first leg call
    // if that's the case, we need to update the warmTransferContext with transferringCallId
    const task = TasksModule.getTaskByCallId(event.callId);
    // make sure that this task is a warm up call of the warm transfer
    if (this.isWarmUpCall(event.callId, event.remoteIdentity)) {
      const warmTransferContext: WarmTransferContext = {
        ...task?.attributes?.warmTransferContext, // task should be there as it's checked by isWarmUpCall
        warmUpCallId: event.callId,
      };

      this.updateWarmTransferContexts(
        warmTransferContext.originalCallId,
        event.callId,
        warmTransferContext,
      );
    }
  }

  /**
   * Handles the phone call answered event for warm transfer tracking.
   *
   * @param event - The call event containing call ID and remote identity information
   *
   * Behavior:
   * - Identifies if the answered call is part of a warm transfer warm up call
   * - Marks the warm up call as connected in the warm transfer context
   * - Updates context across both original and warm up call tasks
   * - Enables UI state updates to reflect successful warm up call connection
   */
  private handlePhoneCallAnswered(event: CallEvent): void {
    // we need to check if the call that is answered is a warm transfer first leg call
    // if that's the case, we need to update the warmTransferContext with transferringCallId
    const task = TasksModule.getTaskByCallId(event.callId);
    // make sure that this task is a warm up call of the warm transfer; otherwise we are not interested in this event
    if (this.isWarmUpCall(event.callId, event.remoteIdentity)) {
      const warmTransferContext: WarmTransferContext = {
        ...task?.attributes.warmTransferContext,
        isWarmUpCallConnected: true,
      };

      this.updateWarmTransferContexts(
        warmTransferContext.originalCallId,
        event.callId,
        warmTransferContext,
      );
    }
  }

  /**
   * Handles phone call ended events for warm transfer cleanup.
   *
   * @param event - The call event containing call ID and remote identity information
   *
   * Behavior:
   * - Identifies warm transfer related calls that have ended
   * - Searches for tasks containing matching warm transfer context
   * - Cleans up warm transfer context from remaining tasks
   * - Prevents orphaned transfer state when calls end unexpectedly
   * - Handles both original and warm up call call endings
   */
  private handlePhoneCallEnded(event: CallEvent): void {
    // it's possible that the task is already deleted, so we need to find the remaining warm transfer task
    // by looking at the warmTransferContext

    const tasks = TasksModule.getTasks();

    // we need to find the warm transfer related tasks (original and warm up)
    // we need to clean up the warmTransferContext from any remaining tasks (it's possible that the warm up one got deleted already by Inbox Module)

    const warmTransferTask = tasks.find(
      (t) =>
        t.attributes?.warmTransferContext?.warmUpCallId === event.callId ||
        t.attributes?.warmTransferContext?.originalCallId === event.callId ||
        t.attributes?.warmTransferContext?.targetPhoneNumber.includes(
          event.remoteIdentity,
        ) ||
        event.remoteIdentity.includes(
          t.attributes?.warmTransferContext?.targetPhoneNumber,
        ),
    );

    if (!warmTransferTask) {
      return; // no task found, nothing to do
    }

    // clean up the warm transfer context from the task
    this.deleteWarmTransferContexts(
      warmTransferTask.attributes.warmTransferContext,
    );
  }
}

export default new TransfersModule(
  appManager.events,
  appManager.logger('Transfers'),
);
